import { useState } from "react";

import { type AdhdVariant, api } from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { useLang } from "../lib/i18n";
import { adhdCopy } from "../lib/i18n/adhd";
import { ScreenShell } from "./screen-shell";
import { Eyebrow } from "./ui/primitives";

// One-time onboarding question for ADHD Home mode (ADR-0015, design handoff
// §"Onboarding decision"). Shown once, after the targets step, only when the
// feature flag is on. The four answers map to {enabled, variant}.
type Answer = { enabled: boolean; variant: AdhdVariant };

type Props = {
  // Called once the choice is persisted (or skipped) — App.tsx continues
  // to Home.
  onDone: () => void;
};

export function AdhdOnboardingScreen({ onDone }: Props) {
  const { lang } = useLang();
  const { updateUser } = useAuth();
  const t = adhdCopy[lang];
  const [submitting, setSubmitting] = useState(false);

  // Krok (variant C) chwilowo schowane z UI — patrz komentarz w adhd-chooser.tsx.
  const answers: Array<{ label: string; value: Answer }> = [
    { label: t.onbAnswerAll, value: { enabled: false, variant: "A" } },
    { label: t.onbAnswerCalm, value: { enabled: true, variant: "A" } },
    { label: t.onbAnswerSignal, value: { enabled: true, variant: "B" } },
  ];

  async function choose(a: Answer) {
    setSubmitting(true);
    // Optimistic local update so Home renders the right mode immediately.
    updateUser({ adhdEnabled: a.enabled, adhdVariant: a.variant });
    try {
      await api.setAdhd(a.enabled, a.variant);
    } catch {
      // Non-fatal: the local update still applies; the user can change it
      // any time from the pill or settings.
    }
    onDone();
  }

  return (
    <ScreenShell title={t.onbEyebrow}>
      <div style={{ marginTop: 28 }}>
        <Eyebrow>{t.onbEyebrow}</Eyebrow>
        <h1
          style={{
            marginTop: 14,
            fontFamily: "var(--font-display)",
            fontSize: 38,
            fontWeight: 800,
            letterSpacing: "-0.04em",
            lineHeight: 1.05,
            color: "var(--ink)",
            maxWidth: 320,
          }}
        >
          {t.onbQuestion}
          <span style={{ color: "var(--accent)" }}>.</span>
        </h1>
      </div>

      <div className="flex flex-col" style={{ gap: 10, marginTop: 28 }}>
        {answers.map((a) => (
          <button
            key={a.label}
            type="button"
            disabled={submitting}
            onClick={() => choose(a.value)}
            className="w-full text-left transition-colors disabled:opacity-60"
            style={{
              padding: "16px 18px",
              borderRadius: 14,
              background: "var(--surface)",
              boxShadow: "0 0 0 1px var(--nav-border)",
              color: "var(--ink)",
              fontSize: 15,
              fontWeight: 600,
            }}
          >
            {a.label}
          </button>
        ))}
      </div>

      <div className="flex justify-center" style={{ marginTop: 18 }}>
        <button
          type="button"
          disabled={submitting}
          onClick={onDone}
          style={{ color: "var(--ink-2)", fontSize: 13, fontWeight: 600 }}
        >
          {submitting ? t.onbSaving : t.onbSkip}
        </button>
      </div>
    </ScreenShell>
  );
}
